import awsConfig from './aws-exports';
import Cookies from 'js-cookie';
import { debounce as _debounce } from 'lodash-es';
import * as Sentry from '@sentry/react';

import { isStaleChunkReloadPending } from './core/helpers/stale-chunk-reload';

// This is injected by the middleware.js
const nonce = typeof document !== 'undefined'
  ? document.querySelector('meta[property="csp-nonce"]')?.getAttribute('content')
  : undefined;

const isCrawler = () =>
  typeof navigator !== 'undefined' &&
  /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);

const AGENCY_CHAT_ID = '3d698c17b45c1575b862ceb645b4b4bd';
const INTERNAL_HELP_DESK_CHAT_ID = '6353596ce5e684d6a05ecc03875b6356';
const TEST_FRONT_CHAT_WITH_KYBER = 'fa80b34c1dacf6801f17c8a6faf477fd';

function initializeSegment() {
  if (isCrawler() || !awsConfig.trackingEnabled) {
    return;
  }
  !(function () {
    var analytics = (window.analytics = window.analytics || []);
    if (!analytics.initialize)
      if (analytics.invoked) window.console && console.error && console.error('Segment snippet included twice.');
      else {
        analytics.invoked = !0;
        analytics.methods = [
          'trackSubmit',
          'trackClick',
          'trackLink',
          'trackForm',
          'pageview',
          'identify',
          'reset',
          'group',
          'track',
          'ready',
          'alias',
          'debug',
          'page',
          'once',
          'off',
          'on'
        ];
        analytics.factory = function (t) {
          return function () {
            var e = Array.prototype.slice.call(arguments);
            e.unshift(t);
            analytics.push(e);
            return analytics;
          };
        };
        for (var t = 0; t < analytics.methods.length; t++) {
          var e = analytics.methods[t];
          analytics[e] = analytics.factory(e);
        }
        analytics.load = function (t, e) {
          var n = document.createElement('script');
          n.type = 'text/javascript';
          n.async = !0;
          n.nonce = nonce;
          n.src = 'https://cdn.segment.com/analytics.js/v1/' + t + '/analytics.min.js';
          var a = document.getElementsByTagName('script')[0];
          a.parentNode.insertBefore(n, a);
          analytics._loadOptions = e;
        };
        analytics.SNIPPET_VERSION = '4.1.0';
        analytics.load(awsConfig.segmentKey, {
          integrations: {
            Appcues: true
          }
        });
        analytics.page();
        const sessionId = Cookies.get('ampSessionId') || Date.now();
        Cookies.set('ampSessionId', sessionId, { expires: 0.5 / 24 }); //session expires in 30 mins
      }
  })();
}

function initializeLogRocket() {
  if (awsConfig.trackingEnabled) {
    import('logrocket').then(({ default: LogRocket }) => {
      LogRocket.init(awsConfig.logRocketApiKey);
      // Add a slight delay to ensure LogRocket is fully initialized
      setTimeout(() => {
        LogRocket.getSessionURL((sessionUrl) => {
          if (sessionUrl) {
            window.analytics.track('Staff Logrocket Session', { logRocketSessionURL: sessionUrl });
          }
        });
      }, 2000);
    });
  }
}

function initializeFriendBuy(merchantId) {
  window['friendbuyAPI'] = [];
  friendbuyAPI = window['friendbuyAPI'];

  friendbuyAPI.merchantId = merchantId;
  friendbuyAPI.push(['merchant', friendbuyAPI.merchantId]);
  // load the merchant SDK and your campaigns
  (function (f, r, n, d, b, u, y) {
    while ((u = n.shift())) {
      (b = f.createElement(r)), (y = f.getElementsByTagName(r)[0]);
      b.async = 1;
      b.src = u;
      b.nonce = nonce;
      y.parentNode.insertBefore(b, y);
    }
  })(document, 'script', [
    'https://static.fbot.me/friendbuy.js',
    'https://campaign.fbot.me/' + friendbuyAPI.merchantId + '/campaigns.js'
  ]);
}

export async function initializeFB() {
  if (awsConfig.friendBuyMerchantId) {
    initializeFriendBuy(awsConfig.friendBuyMerchantId);
  }
}

export const getFrontChatId = (session) => {
  if (awsConfig.stackeryEnvironmentName.includes('uat')) {
    return TEST_FRONT_CHAT_WITH_KYBER;
  }
  return !session || session?.isAgency ? AGENCY_CHAT_ID : INTERNAL_HELP_DESK_CHAT_ID;
};

/**
 * Initialize front chat and identify user in the chat.
 * If no session, still inits front chat with no session in case an external agent is having issues logging in.
 *
 * Cannot rapidly fire the identify after init or it will not work, so use setTimeout to space out the calls.
 *
 * @param {Object} session - session context object
 *
 */
export const initializeFrontChatAndIdentify = _debounce((session) => {
  try {
    console.log(`initializing front chat for session: ${JSON.stringify({ isAgency: session?.isAgency })}`);

    const chatId = getFrontChatId(session);

    // Wait for script to load with polling mechanism
    const waitForFrontChat = (callback, maxAttempts = 10, attempt = 0) => {
      if (typeof window.FrontChat === 'function') {
        callback();
      } else if (attempt < maxAttempts) {
        setTimeout(() => waitForFrontChat(callback, maxAttempts, attempt + 1), 500);
      } else {
        console.warn('FrontChat script failed to load after multiple attempts');
      }
    };

    waitForFrontChat(() => {
      try {
        window.FrontChat('init', {
          chatId,
          useDefaultLauncher: false,
          nonce
        });

        // Identity after init
        setTimeout(() => {
          identifyUserInFront(session?.user);
        }, 1000);
      } catch (error) {
        console.warn('Error during FrontChat initialization:', error);
        Sentry.captureException(error);
      }
    });
  } catch (error) {
    console.log(`error initializing front chat: ${JSON.stringify(error)}`);
    Sentry.captureException(error);
  }
}, 100);

export function identifyUserInFront(user) {
  if (user && typeof window.FrontChat === 'function') {
    try {
      console.log(`identifying user in front: ${JSON.stringify({ username: user.username, email: user.email })}`);
      window.FrontChat('identity', {
        name: user.username,
        email: user.email,
        nonce
      });
    } catch (error) {
      console.warn('Error identifying user in FrontChat:', error);
    }
  } else if (user) {
    console.warn('FrontChat not loaded yet, skipping user identification');
  }
}

function initializeUpscope() {
  // This will initiate Upscope connection. It's important it is added to all pages,
  // even when the user is not logged in.
  // You can place this in the head or the body of the page.
  (function (w, u, d) {
    var i = function () {
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    var l = function () {
      var s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.nonce = nonce;
      s.src = 'https://code.upscope.io/PZsehcBK2C.js';
      var x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };
    if (typeof u !== 'function') {
      w.Upscope = i;
      l();
    }
  })(window, window.Upscope, document);
  Upscope('init', { useInlineCss: false });
}

export function identifyUserInUpscope(user) {
  // If the user is logged in, optionally identify them with the following method.
  // You can call Upscope('updateConnection', {}); at any time.
  Upscope('updateConnection', {
    // Set the user ID below. If you don't have one, set to undefined.
    uniqueId: user.username,
    // Set the user name or email below (e.g. ["John Smith", "john.smith@acme.com"]).
    identities: [user.email]
  });
}

export function identifyUserInAppcues(session) {
  try {
    if (window.Appcues && session?.user) {
      window.Appcues.identify(`staff-${session.user.username}`, {
        email: session.user.email,
        isAgency: session.isAgency,
        canSellAndService: session.user.canSellAndService
      });
    }
  } catch (error) {
    console.warn('[sentry] Failed identifying user in Appcues', error);
    Sentry.captureException(error);
  }
}

function initializeSentry() {
  if (import.meta.env.VITE_SENTRY_ENABLED === 'true') {
    Sentry.init({
      dsn: 'https://92e31532c8f0419eb6c9d3ce2f620928@o198753.ingest.us.sentry.io/1501891',
      environment: import.meta.env.VITE_STACKERY_ENVIRONMENT,
      ignoreErrors: ['ResizeObserver loop limit exceeded', 'NotAuthorizedException', 'subscriptionFailedCallback'],
      // Third-party scripts throw when they touch a page that is being torn down (a stale-chunk
      // reload or navigation removes document.body); none of it is actionable from here.
      // Hosts are the script-src entries in middleware.js (STAFF-FE-BJAT/BJAW/BJAV, #36444).
      denyUrls: [
        /^https:\/\/js\.stripe\.com\//,
        /^https:\/\/[a-z0-9.-]+\.upscope\.io\//,
        /^https:\/\/[a-z0-9.-]+\.appcues\.(com|net)\//
      ],
      // Documentation: https://docs.sentry.io/platforms/javascript/configuration/filtering/
      beforeSend(event) {
        try {
          // A stale-chunk reload is under way and the page is about to be replaced. Errors in this
          // window come from code unwrapping the prevented import's undefined module (#36444).
          if (isStaleChunkReloadPending()) {
            return null;
          }
          // Level: error
          if (event.level === 'error' && event.exception?.values) {
            const failedToFetchException = event.exception.values.some(
              (v) => v.type === 'TypeError' && v.value === 'Failed to fetch'
            );
            // Exception: TypeError: Failed to fetch
            if (failedToFetchException && event.breadcrumbs) {
              const audiencepsynchBreadcrumb = event.breadcrumbs.find(
                (b) =>
                  b.category === 'fetch' &&
                  b.data &&
                  b.data.method === 'POST' &&
                  b.data.url?.includes('analytics2.audiencepsynch.com/api/v1/pixel-data/json')
              );
              // audiencepsynch endpoint
              if (audiencepsynchBreadcrumb) {
                // Ignore event
                return null;
              }
            }
          }
          return event;
        } catch (error) {
          console.warn('[sentry] Failed checking if event should be ignored', error);
          return event;
        }
      }
    });
  }
}

export default function configureServices() {
  initializeSegment();
  initializeLogRocket();
  initializeUpscope();
  initializeSentry();
}
